import { ReactElement, memo, useMemo, useState } from 'react';

import { IssueCard } from 'components/issue/Card';
import { Spinner } from 'components/core';
import { Box } from '@mui/material';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { useProtocolOnViewForChecklistItem } from 'components/inspection/protocolLookup';
import {
  useInspection,
  useInspectionTemplateOnView,
  useInspectionFieldDisabled,
  isInspectionFieldDisabled,
} from 'store/inspection';
import { useProtocolItemIssuesList } from 'store/issue';
import { isNonEmptyArray } from 'shared/utils/array';
import { SetableIssueFields } from 'views/issue/wizard/types';

import { MemoProtocolItemIssuesPresentational } from './presentational';
import { ProtocolItemIssuesProps } from './types';
import { useProtocolItemIssues } from './hooks/useProtocolItemIssues';
import { issueCardLoadingSlotSx, issueCardSlotSx } from './styles';

const DISABLED_FIELDS: (keyof SetableIssueFields)[] = ['process'];

function ProtocolItemIssues(props: ProtocolItemIssuesProps): ReactElement {
  const {
    checklistItemId,
    allExpanded,
    belowToolbar,
    attachmentsSection,
    alwaysShowSecondaryColumns,
  } = props;

  const { template } = useInspectionTemplateOnView();
  const { inspection } = useInspection();
  const { values } = useInputForm();
  const protocolItem = useProtocolOnViewForChecklistItem(checklistItemId);

  const { issues, loading } = useProtocolItemIssuesList(protocolItem?._id);

  const [open, setOpen] = useState(false);

  const { issueCardOnClick, handleClose, createInspectionIssue, beforeSave } =
    useProtocolItemIssues({
      checklistItemId,
      allExpanded,
      inspection,
      protocolItem,
      setOpen,
    });

  const sites = values.sites as { _id: string; label: string }[] | undefined;
  const firstSite = sites?.[0];
  const initialValues = useMemo(() => ({ site: firstSite }), [firstSite]);
  const resolveFieldDisabled = useInspectionFieldDisabled();
  const formDisabled = resolveFieldDisabled(false);
  const isFormReadOnly = isInspectionFieldDisabled(formDisabled);
  const isCreationDisabled = !firstSite || !isNonEmptyArray(values.levels);

  let creationDisabledReason: string | undefined;
  if (isFormReadOnly) {
    creationDisabledReason =
      typeof formDisabled === 'object' ? formDisabled.reason : undefined;
  } else if (isCreationDisabled) {
    creationDisabledReason = 'protocol_item_create_issue_site_empty_warning';
  }

  const issueCards = useMemo((): ReactElement[] => {
    if (loading && issues.length === 0) {
      return [
        <Box key='loading' sx={issueCardLoadingSlotSx}>
          <Spinner reason='ProtocolItemIssues loading issues' />
        </Box>,
      ];
    }

    return issues.map((issue) => (
      <Box key={issue._id} sx={issueCardSlotSx}>
        <IssueCard onClick={issueCardOnClick} issue={issue} variant='square' />
      </Box>
    ));
  }, [issues, issueCardOnClick, loading]);

  const processId = template?.process;
  const processCode = template?.renderableProcess?.code;

  return (
    <MemoProtocolItemIssuesPresentational
      issueCards={issueCards}
      createInspectionIssue={createInspectionIssue}
      processId={processId}
      processCode={processCode}
      initialValues={initialValues}
      disabledFields={DISABLED_FIELDS}
      open={open}
      handleClose={handleClose}
      disabled={isCreationDisabled || isFormReadOnly}
      disabledReason={creationDisabledReason}
      beforeSave={beforeSave}
      belowToolbar={belowToolbar}
      attachmentsSection={attachmentsSection}
      alwaysShowSecondaryColumns={alwaysShowSecondaryColumns}
    />
  );
}

export const MemoProtocolItemIssues = memo(ProtocolItemIssues);
